import { useOwnershipLabels } from "../../../OwnershipLabels";
import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";
import CountrySelect from "../../../../components/ui/CountrySelect";
import DatePickerField from "../../../../components/ui/DatePickerField";
import IndividualNamePreview from "../../../../components/clients/IndividualNamePreview";
import { useLockBodyScroll } from "../common_utils/useLockBodyScroll";

import iconIndividual from "../../../../images/avatar.svg";
import iconEntity from "../../../../images/businessTrade.svg";
import iconTrust from "../../../../images/certificate.svg";
import iconProduction from "../../../../images/icon-production.svg";
import iconHolding from "../../../../images/icon-holding.svg";
import iconPic from "../../../../images/icon-pic.svg";
import iconQuoted from "../../../../images/icon-quoted.svg";
import iconBo from "../../../../images/buildpers.svg";
import iconPercent from "../../../../images/analytics.svg";
import iconPassport from "../../../../images/passport.svg";
import iconAddress from "../../../../images/earth-location.svg";
import iconDateOfBirth from "../../../../images/birthday.svg";
import iconRightArrow from "../../../../images/right-arrow.svg";
import iconQualifiedUbo from "../../../../images/qualified-g.svg";
import { isQualifiedUboRow } from "../../../../utils/qualifiedUboMarkers";
import DocumentPrefillMark from "../DocumentPrefillMark";

/* -------------------- helpers -------------------- */

function safeClone(obj) {
  try {
    if (typeof structuredClone === "function") return structuredClone(obj);
  } catch (_) {}
  return JSON.parse(JSON.stringify(obj ?? {}));
}

function makeRowId(prefix = "row") {
  return `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function sanitizePercent(v) {
  let s = String(v ?? "").replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  if (s === "") return "";
  const n = Number(s);
  if (Number.isNaN(n)) return "";
  const clamped = Math.max(0, Math.min(100, n));
  return String(clamped);
}

function percentNumber(v) {
  const n = Number(String(v ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatPercentValue(n) {
  if (!Number.isFinite(n)) return "";
  const rounded = Math.round(n * 100) / 100;
  return String(rounded);
}

function toTitleCase(value) {
  return String(value || "").replace(/(^|\s)\S/g, (letter) =>
    letter.toUpperCase()
  );
}

function emptyShareholderRow(holderType = "Individual") {
  return {
    _rowId: makeRowId("sh"),
    name: "",
    holderType,
    beneficialOwnerType: "Beneficial owner",
    nationality: "",
    countryOfResidence: "",
    ownershipPercent: "",
  };
}

const displayNames = new Intl.DisplayNames(["en"], { type: "region" });
function countryLabel(code) {
  if (!code) return "";
  try {
    return displayNames.of(String(code).toUpperCase()) || code;
  } catch {
    return code;
  }
}

function toDobDate(value) {
  if (!value) return null;
  let date = null;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value?.toDate === "function") {
    date = value.toDate();
  } else if (typeof value?.seconds === "number") {
    date = new Date(value.seconds * 1000);
  } else if (typeof value === "string" || typeof value === "number") {
    date = new Date(value);
  }
  return !date || Number.isNaN(date.getTime()) ? null : date;
}

function formatTinyDob(value) {
  const date = toDobDate(value);
  if (!date) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }).replace(/\//g, ".");
}

function QualifiedUboBadge({ className = "kycQualifiedUboAvatarBadge" } = {}) {
  return (
    <img
      src={iconQualifiedUbo}
      alt="Qualified UBO"
      title="Qualified UBO"
      className={className}
    />
  );
}

const ENTITY_TYPE_OPTIONS = [
  {
    value: "Non Quoted Operational Company",
    title: "Non quoted operating company",
    hint: "Standard company carrying out commercial business activities.",
    iconSrc: iconProduction,
  },
  {
    value: "PIC",
    title: "Domiciliary company (PIC)",
    hint: "Personal investment company used to hold assets or investments.",
    iconSrc: iconPic,
  },
  {
    value: "Holding",
    title: "Holding",
    hint: "Holding company primarily used to hold interests in companies or assets.",
    iconSrc: iconHolding,
  },
  {
    value: "Quoted Company",
    title: "Quoted company",
    hint: "Company listed on a regulated stock exchange.",
    iconSrc: iconQuoted,
  },
];

const HOLDING_TYPE_OPTIONS = [
  {
    value: "HoldingActive",
    label: "Active holding",
    hint:
      "Holds directly or indirectly a majority stake in one or more operating companies. Main purpose is NOT the administration of third-party assets.",
  },
  {
    value: "HoldingPassive",
    label: "Passive holding",
    hint:
      "Mainly combines and manages assets of a family or a defined group. Often used to enable dividend distributions.",
  },
];

function entitySubtypeFlags(entityType) {
  return {
    shareholderEntityType: entityType,
    isPIC: entityType === "PIC",
    isHoldingPassive: entityType === "HoldingPassive",
    isHoldingActive: entityType === "HoldingActive",
    isPicOrPassiveHolding: entityType === "PIC" || entityType === "HoldingPassive",
  };
}

function EntityTypeModal({ onClose, onSelect }) {
  const { ownershipLabel } = useOwnershipLabels();
  const [holdingSelectorOpen, setHoldingSelectorOpen] = useState(false);

  return createPortal(
    <div
      className="kycShareholderEntityTypeModalBackdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="kycShareholderEntityTypeModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kycShareholderEntityTypeModalTitle"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="kycShareholderEntityTypeModalHead">
          <div>
            <div
              id="kycShareholderEntityTypeModalTitle"
              className="kycShareholderEntityTypeModalTitle"
            >
              Select entity type
            </div>
            <div className="kycShareholderEntityTypeModalHint">
              Choose the type of entity to add as a direct shareholder.
            </div>
          </div>
          <button
            type="button"
            className="kycShareholderEntityTypeModalClose"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        <div className="kycShareholderEntityTypeGrid">
          {ENTITY_TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`kycShareholderEntityTypeChoice${
                option.value === "Holding" && holdingSelectorOpen
                  ? " is-active"
                  : ""
              }`}
              onClick={() => {
                if (option.value === "Holding") {
                  setHoldingSelectorOpen(true);
                  return;
                }
                onSelect?.(option.value);
              }}
            >
              <span className="kycShareholderEntityTypeChoiceIcon">
                <img src={option.iconSrc} alt="" aria-hidden="true" />
              </span>
              <span className="kycShareholderEntityTypeChoiceCopy">
                <span className="kycShareholderEntityTypeChoiceTitle">
                  {ownershipLabel(option.title)}
                </span>
                <span className="kycShareholderEntityTypeChoiceHint">
                  {option.hint}
                </span>
              </span>
            </button>
          ))}
        </div>

        <div
          className={`kycShareholderHoldingTypeReveal${
            holdingSelectorOpen ? " is-open" : ""
          }`}
          aria-hidden={!holdingSelectorOpen}
        >
          <div className="kycShareholderHoldingTypeCard">
            <div className="kycShareholderHoldingTypeHead">
              <strong>Holding type</strong>
              <p>Specify whether the holding company is active or passive.</p>
            </div>
            <div className="kycShareholderHoldingTypeGrid">
              {HOLDING_TYPE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onSelect?.(option.value)}
                >
                  <strong>{option.label}</strong>
                  <span>{option.hint}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* -------------------- component -------------------- */

export default function DirectShareholdersTable({
  readOnly,
  previewSeparateNames = false,
  entityName = "Account holder",

  shareholders = [],
  // Rows filled from the company documents and unchanged since ("From your documents" mark).
  prefilledRowIds = [],
  individualsOnly = false,
  legalArrangements = [],
  onSetAt,

  // ✅ PIC: hide % column completely
  hidePercentColumn = false,

  // optional: style hooks
  wrapClassName = "",
  changedRowIds = [],

  // client desktop: render cards instead of table
  clientMode = false,
  qualifiedNodeIds = new Set(),
  // Subtle badge next to the title (e.g. "Please enter each shareholder's percentage.").
  titleNote = "",
  // Client e-form: dates of birth are taken by staff from the identity documents.
  hideDateOfBirth = false,
}) {
  const [openTypeRowId, setOpenTypeRowId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [openCapacityRowId, setOpenCapacityRowId] = useState(null);
  const [capacityDropdownPos, setCapacityDropdownPos] = useState({ top: 0, left: 0 });
  const [entityTypeModalOpen, setEntityTypeModalOpen] = useState(false);
  useLockBodyScroll(entityTypeModalOpen, { strategy: "overflow" });
  const typeBtnRefs = useRef({});
  const capacityBtnRefs = useRef({});
  const canEditShareholderType = !readOnly && !clientMode;
  const changedRowIdSet = new Set(
    (Array.isArray(changedRowIds) ? changedRowIds : []).map((id) =>
      String(id || "")
    )
  );

  const TYPE_OPTIONS = ["Individual", "Entity", "Trust", "Foundation"];
  const ADD_SHAREHOLDER_OPTIONS = [
    { holderType: "Individual", label: "Add natural person" },
    { holderType: "Entity", label: "Add entity", opensEntityModal: true },
    { holderType: "Trust", label: "Add trust" },
    { holderType: "Foundation", label: "Add foundation" },
  ];

  function toggleTypeDropdown(rowId) {
    if (!canEditShareholderType) return;
    if (openTypeRowId === rowId) {
      setOpenTypeRowId(null);
      return;
    }
    const btn = typeBtnRefs.current[rowId];
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpenTypeRowId(rowId);
  }

  function toggleCapacityDropdown(rowId) {
    if (readOnly) return;
    if (openCapacityRowId === rowId) {
      setOpenCapacityRowId(null);
      return;
    }
    const btn = capacityBtnRefs.current[rowId];
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setCapacityDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    setOpenCapacityRowId(rowId);
  }

  useLayoutEffect(() => {
    if (!openTypeRowId && !openCapacityRowId) return undefined;

    const updateDropdownPositions = () => {
      if (openTypeRowId) {
        const rect =
          typeBtnRefs.current[openTypeRowId]?.getBoundingClientRect?.();
        if (!rect) {
          setOpenTypeRowId(null);
        } else {
          setDropdownPos({ top: rect.bottom + 4, left: rect.left });
        }
      }

      if (openCapacityRowId) {
        const rect =
          capacityBtnRefs.current[
            openCapacityRowId
          ]?.getBoundingClientRect?.();
        if (!rect) {
          setOpenCapacityRowId(null);
        } else {
          setCapacityDropdownPos({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
          });
        }
      }
    };

    updateDropdownPositions();
    window.addEventListener("scroll", updateDropdownPositions, true);
    window.addEventListener("resize", updateDropdownPositions);
    return () => {
      window.removeEventListener("scroll", updateDropdownPositions, true);
      window.removeEventListener("resize", updateDropdownPositions);
    };
  }, [openTypeRowId, openCapacityRowId]);

  const actionTh = !readOnly ? <th className="kycTblActionsTh" /> : null;

  const TrashBtn = ({ onClick, disabled }) => (
    <button
      type="button"
      className="btn btn-sm btn-delete-trash p-0 d-inline-flex align-items-center justify-content-center"
      aria-label="Remove row"
      title="Remove row"
      onClick={onClick}
      disabled={disabled}
    >
      <i className="bi bi-trash" />
    </button>
  );

  const BENEFICIAL_OWNER_TYPE_OPTIONS = [
    { v: "Beneficial owner", label: "Beneficial owner" },
    { v: "Nominee shareholder", label: "Nominee shareholder" },
  ];

  function capacityLabel(value) {
    if (!hidePercentColumn) {
      return value === "Nominee shareholder" ? "Nominee" : "Beneficial Own.";
    }
    const found = BENEFICIAL_OWNER_TYPE_OPTIONS.find(
      (o) => String(o.v) === String(value || "")
    );
    return found?.label || "Beneficial owner";
  }

  const percentInputProps = {
    inputMode: "decimal",
    pattern: "^\\d+(\\.\\d+)?$",
    placeholder: "0–100",
  };

  function setShareholders(next) {
    if (readOnly) return;
    onSetAt?.(["ownership", "shareholders"], next);
  }

  function ensureRowId(row, idx) {
    if (row?._rowId) return row._rowId;
    const next = safeClone(shareholders);
    next[idx] = { ...(next[idx] || {}), _rowId: makeRowId("sh") };
    setShareholders(next);
    return next[idx]._rowId;
  }

  function updateRow(idx, key, value) {
    if (readOnly) return;
    const next = safeClone(shareholders);

    if (!next[idx]) next[idx] = {};
    if (!next[idx]._rowId) next[idx]._rowId = makeRowId("sh");

    next[idx] = typeof key === "object" ? { ...next[idx], ...key } : { ...next[idx], [key]: value };
    setShareholders(next);
  }

  function updatePercent(idx, raw) {
    const sanitized = sanitizePercent(raw);
    if (sanitized === "") {
      updateRow(idx, "ownershipPercent", "");
      return;
    }

    const otherTotal = (shareholders || []).reduce((sum, row, rowIdx) => {
      if (rowIdx === idx) return sum;
      return sum + percentNumber(row?.ownershipPercent);
    }, 0);
    const maxForRow = Math.max(0, Math.round((100 - otherTotal) * 100) / 100);
    const nextValue = Math.min(percentNumber(sanitized), maxForRow);

    updateRow(idx, "ownershipPercent", formatPercentValue(nextValue));
  }

  function updateShareholderType(idx, value) {
    const next = safeClone(shareholders);
    if (!next[idx]) next[idx] = {};
    if (!next[idx]._rowId) next[idx]._rowId = makeRowId("sh");

    next[idx].holderType = value;
    if (String(value || "").trim().toLowerCase() !== "entity") {
      next[idx].shareholderEntityType = "";
      next[idx].isPIC = false;
      next[idx].isHoldingPassive = false;
      next[idx].isHoldingActive = false;
      next[idx].isPicOrPassiveHolding = false;
    } else if (!next[idx].shareholderEntityType) {
      Object.assign(
        next[idx],
        entitySubtypeFlags("Non Quoted Operational Company")
      );
    }

    const t = String(value || "")
      .trim()
      .toLowerCase();
    const isEntityOrTrustLike =
      t === "entity" || t === "trust" || t === "foundation";
    if (isEntityOrTrustLike) next[idx].nationality = "";

    setShareholders(next);
  }

  function arrangementForTrustShareholder(row, rowId) {
    const arrangementType = String(row?.holderType || "").trim().toLowerCase();
    if (arrangementType !== "trust" && arrangementType !== "foundation") {
      return { arrangement: null, arrangementIdx: -1 };
    }

    const normalizedName = String(row?.name || "").trim().toLowerCase();
    const list = Array.isArray(legalArrangements) ? legalArrangements : [];

    let arrangementIdx = list.findIndex((arr) => {
      const type = String(arr?.arrangementType || "").trim().toLowerCase();
      return (
        type === arrangementType &&
        String(arr?.source || "").trim().toLowerCase() === "shareholder" &&
        String(arr?.sourceRefRowId || "") === String(rowId || "")
      );
    });

    if (arrangementIdx === -1 && normalizedName) {
      arrangementIdx = list.findIndex((arr) => {
        const type = String(arr?.arrangementType || "").trim().toLowerCase();
        return (
          type === arrangementType &&
          String(arr?.name || "").trim().toLowerCase() === normalizedName
        );
      });
    }

    return {
      arrangement: arrangementIdx >= 0 ? list[arrangementIdx] : null,
      arrangementIdx,
    };
  }

  function trusteeDomicileForShareholder(row, rowId) {
    const { arrangement } = arrangementForTrustShareholder(row, rowId);
    const trustee = (arrangement?.parties || []).find(
      (party) => String(party?.role || "").trim().toLowerCase() === "trustee"
    );
    return String(trustee?.domicile || "").trim();
  }

  function updateTrusteeDomicileForShareholder(row, rowId, value) {
    const { arrangementIdx } = arrangementForTrustShareholder(row, rowId);
    if (arrangementIdx < 0) return false;

    const nextArrangements = safeClone(legalArrangements);
    const parties = Array.isArray(nextArrangements[arrangementIdx]?.parties)
      ? nextArrangements[arrangementIdx].parties
      : [];
    const trusteeIdx = parties.findIndex(
      (party) => String(party?.role || "").trim().toLowerCase() === "trustee"
    );
    if (trusteeIdx < 0) return false;

    parties[trusteeIdx] = {
      ...(parties[trusteeIdx] || {}),
      domicile: value,
    };
    nextArrangements[arrangementIdx] = {
      ...(nextArrangements[arrangementIdx] || {}),
      parties,
    };
    onSetAt?.(["ownership", "legalArrangements"], {
      arrangements: nextArrangements,
    });
    return true;
  }

  function addRow(holderType = "Individual") {
    if (readOnly) return;
    const next = safeClone(shareholders);
    next.push(emptyShareholderRow(holderType));
    setShareholders(next);
  }

  function addEntityRow(entityType) {
    if (readOnly) return;
    const next = safeClone(shareholders);
    next.push({
      ...emptyShareholderRow("Entity"),
      ...entitySubtypeFlags(entityType),
    });
    setShareholders(next);
    setEntityTypeModalOpen(false);
  }

  function AddShareholderButtons({ clientStyle = false }) {
    return (
      <div className="kycDirectShareholderAddActions">
        {ADD_SHAREHOLDER_OPTIONS.filter(option => !individualsOnly || option.holderType === "Individual").map((option) => (
          <button
            key={option.holderType}
            type="button"
            className={
              clientStyle ? "kycSigAddBtn" : "btn btn-sm btn-outline-secondary"
            }
            onClick={() => {
              if (option.opensEntityModal) {
                setEntityTypeModalOpen(true);
                return;
              }
              addRow(option.holderType);
            }}
          >
            <i className="bi bi-plus-lg me-2" aria-hidden="true" />
            {option.label}
          </button>
        ))}
      </div>
    );
  }

  function deleteRow(rowId) {
    if (readOnly) return;
    const next = safeClone(shareholders).filter(
      (r) => String(r?._rowId || "") !== String(rowId || "")
    );
    setShareholders(next);
  }

  /* ---- CLIENT MODE: card-based layout (same as view) ---- */
  if (clientMode) {
    return (
      <>
        {entityTypeModalOpen ? (
          <EntityTypeModal
            onClose={() => setEntityTypeModalOpen(false)}
            onSelect={addEntityRow}
          />
        ) : null}
        <div className="d-flex justify-content-between align-items-end mb-2">
          <div className="OwnershipMainSectionTitle">
            Direct shareholders of {entityName}
            {titleNote ? <span className="kycOwnershipTitleNote" role="status">{titleNote}</span> : null}
          </div>
        </div>

        <div
          className={"d-grid mb-2 " + (wrapClassName || "")}
          style={{ gap: 12 }}
        >
          {(shareholders || []).map((r, idx) => {
            const rowId = ensureRowId(r, idx);

            const type = String(r?.holderType ?? "")
              .trim()
              .toLowerCase();
            const isEntity = type === "entity";
            const isTrust = type === "trust";
            const isFoundation = type === "foundation";
            const isTrustLike = isTrust || isFoundation;
            const isEntityLike = isEntity || isTrustLike;
            const displayedCountryOfResidence = isTrust
              ? trusteeDomicileForShareholder(r, rowId) ||
                String(r?.countryOfResidence || "")
              : String(r?.countryOfResidence || "");
            const dobLabel =
              !isEntityLike && formatTinyDob(r?.dobOrIncorporationDate);

            const identityIcon = isEntity
              ? iconEntity
              : isTrustLike
              ? iconTrust
              : iconIndividual;
            const isQualified = isQualifiedUboRow(r, qualifiedNodeIds);

            return (
              <div
                key={rowId}
                className={
                  "kycOwnershipPartyCard kycOwnership4ColCard kycShareholdersCard kycShEditCardWrap" +
                  (!hidePercentColumn ? " kycShEditCardWithPercent" : "") +
                  (hideDateOfBirth ? " kycShNoDob" : "") +
                  (changedRowIdSet.has(String(rowId)) ? " kycChangedWrap" : "")
                }
              >
                {changedRowIdSet.has(String(rowId)) ? (
                  <img
                    src={iconRightArrow}
                    className="kycChangedRowArrow kycChangedRowArrow--left"
                    alt=""
                    aria-hidden="true"
                  />
                ) : null}
                {!readOnly ? (
                  <button
                    type="button"
                    className="kycShEditDeleteBtn"
                    onClick={() => deleteRow(rowId)}
                    aria-label="Remove shareholder"
                  >
                    <i className="bi bi-trash3" aria-hidden="true" />
                  </button>
                ) : null}

                {/* Col 1: Identity — type label (clickable) + name input */}
                <div className="kycOwnershipCell kycOwnershipCellIdentity_beige">
                  <div className="kycInfoItem kycInfoItemAlignTop">
                    <div
                      className={`kycInfoIconDarkBeige${
                        isQualified ? " kycQualifiedUboAvatarIconBox" : ""
                      }`}
                      style={
                        canEditShareholderType ? { cursor: "pointer" } : undefined
                      }
                      onClick={
                        canEditShareholderType
                          ? () => toggleTypeDropdown(rowId)
                          : undefined
                      }
                    >
                      <img src={identityIcon} alt="" className="kycInfoIconImg" />
                      {isQualified ? <QualifiedUboBadge /> : null}
                    </div>
                    <div className="kycInfoContent">
                      <div className="kycShTypeSelectWrap">
                        <button
                          ref={(el) => {
                            typeBtnRefs.current[rowId] = el;
                          }}
                          type="button"
                          className="kycShTypeBtn"
                          style={!canEditShareholderType ? { cursor: "default" } : undefined}
                          onClick={
                            canEditShareholderType
                              ? () => toggleTypeDropdown(rowId)
                              : undefined
                          }
                        >
                          {r?.holderType || "—"}
                          <span className="kycShTypeArrow" aria-hidden="true">
                            ▾
                          </span>
                        </button>



                        {canEditShareholderType &&
                          openTypeRowId === rowId &&
                          createPortal(
                            <>
                              <div
                                className="kycShTypeDismiss"
                                onClick={() => setOpenTypeRowId(null)}
                              />
                              <div
                                className="kycShTypeDropdown"
                                style={{
                                  position: "fixed",
                                  top: dropdownPos.top,
                                  left: dropdownPos.left,
                                }}
                              >
                                {TYPE_OPTIONS.map((opt) => (
                                  <button
                                    key={opt}
                                    type="button"
                                    className={
                                      "kycShTypeOption" +
                                      (r?.holderType === opt ? " is-active" : "")
                                    }
                                    onClick={() => {
                                      updateShareholderType(idx, opt);
                                      setOpenTypeRowId(null);
                                    }}
                                  >
                                    {opt}
                                  </button>
                                ))}
                              </div>
                            </>,
                            document.body
                          )}
                      </div>
                      <div className="kycQualifiedUboInputWrap">
                        {previewSeparateNames && type === "individual" && !readOnly ? (
                          <IndividualNamePreview
                            inputClass="form-control kycCustomerDetailsHybridInput--line"
                            displayName={r?.name || ""}
                            value={r} onChange={(patch) => updateRow(idx, patch)}
                          />
                        ) : (
                        <input
                          className="form-control kycCustomerDetailsHybridInput--line"
                          value={toTitleCase(r?.name ?? "")}
                          disabled={readOnly}
                          onChange={(e) =>
                            updateRow(idx, "name", toTitleCase(e.target.value))
                          }
                        />
                        )}
                        <DocumentPrefillMark show={prefilledRowIds.includes(r?._rowId)} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Col 2: Control — shareholding capacity + % */}
                <div className="kycOwnershipCell kycOwnershipCellShareholderControl">
                  <div className="kycShareholderControlGrid" style={hidePercentColumn ? { gridTemplateColumns: "1fr" } : undefined}>
                    <div className="kycOwnershipCell">
                      <div className="kycInfoItem kycInfoItemAlignTop">
                        <div className="kycInfoIconLightGrey">
                          <img
                            src={iconBo}
                            alt=""
                            className="kycInfoIconImg"
                          />
                        </div>
                        <div className="kycInfoContent">
                          <div className="kycInfoViewLabel">Capacity</div>
                          {clientMode ? (
                            <>
                              <button
                                ref={(el) => {
                                  capacityBtnRefs.current[rowId] = el;
                                }}
                                type="button"
                                className="kycShCapacityBtn kycCustomerDetailsHybridInput--line"
                                disabled={readOnly}
                                onClick={() => toggleCapacityDropdown(rowId)}
                              >
                                <span className="kycShCapacityBtnText">
                                  {capacityLabel(r?.beneficialOwnerType)}
                                </span>
                                <span className="kycShTypeArrow" aria-hidden="true">
                                  ▾
                                </span>
                              </button>

                              {!readOnly &&
                                openCapacityRowId === rowId &&
                                createPortal(
                                  <>
                                    <div
                                      className="kycShTypeDismiss"
                                      onClick={() => setOpenCapacityRowId(null)}
                                    />
                                    <div
                                      className="kycShTypeDropdown kycShCapacityDropdown"
                                      style={{
                                        position: "fixed",
                                        top: capacityDropdownPos.top,
                                        left: capacityDropdownPos.left,
                                        minWidth: capacityDropdownPos.width || 170,
                                      }}
                                    >
                                      {BENEFICIAL_OWNER_TYPE_OPTIONS.map((o) => (
                                        <button
                                          key={o.v}
                                          type="button"
                                          className={
                                            "kycShTypeOption" +
                                            (String(r?.beneficialOwnerType || "Beneficial owner") === String(o.v)
                                              ? " is-active"
                                              : "")
                                          }
                                          onClick={() => {
                                            updateRow(idx, "beneficialOwnerType", o.v);
                                            setOpenCapacityRowId(null);
                                          }}
                                        >
                                          {o.label}
                                        </button>
                                      ))}
                                    </div>
                                  </>,
                                  document.body
                                )}
                            </>
                          ) : (
                            <select
                              className="form-select kycCustomerDetailsHybridInput--line"
                              value={
                                r?.beneficialOwnerType ?? "Beneficial owner"
                              }
                              disabled={readOnly}
                              onChange={(e) =>
                                updateRow(
                                  idx,
                                  "beneficialOwnerType",
                                  e.target.value
                                )
                              }
                            >
                              {BENEFICIAL_OWNER_TYPE_OPTIONS.map((o) => (
                                <option key={o.v} value={o.v}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    </div>

                    {!hidePercentColumn ? (
                      <div className="kycOwnershipCell">
                        <div className="kycInfoItem kycInfoItemAlignTop">
                          <div className="kycInfoIconLightGrey">
                            <img
                              src={iconPercent}
                              alt=""
                              className="kycInfoIconImg"
                            />
                          </div>
                          <div className="kycInfoContent">
                            <div className="kycInfoViewLabel">%</div>
                            <input
                              className="form-control kycCustomerDetailsHybridInput--line"
                              value={r?.ownershipPercent ?? ""}
                              disabled={readOnly}
                              inputMode="decimal"
                              pattern="^\d+(\.\d+)?$"
                              onChange={(e) =>
                                updatePercent(idx, e.target.value)
                              }
                            />
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Date of birth uses the same icon, label and input layout as the other fields. */}
                {hideDateOfBirth ? null : (
                <div className="kycOwnershipCell kycShDobCell">
                  {type === 'individual' && (
                    <div className="kycInfoItem kycInfoItemAlignTop">
                      <div className="kycInfoIconLightGrey">
                        <img src={iconDateOfBirth} alt="" className="kycInfoIconImg" />
                      </div>
                      <div className="kycInfoContent">
                        <label className="kycInfoViewLabel" htmlFor={`sh-dob-${rowId}`}>Date of birth</label>
                        {readOnly ? <span className="kycInfoViewText">{dobLabel || '—'}</span> : (
                          <DatePickerField
                            inputId={`sh-dob-${rowId}`}
                            formatDisplayValue={formatTinyDob}
                            value={toDobDate(r?.dobOrIncorporationDate)}
                            onChange={(value) => updateRow(idx, 'dobOrIncorporationDate', value)}
                            placeholder=""
                            maxDate={new Date()}
                            hideTriggerButton
                            stagedView
                            className="kycShDobPicker"
                            inputClassName="kycCustomerDetailsHybridInput--line"
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* Col 3: Nationality */}
                <div className="kycOwnershipCell">
                  <div className="kycInfoItem kycInfoItemAlignTop">
                    <div className="kycInfoIconLightGrey">
                      <img
                        src={iconPassport}
                        alt=""
                        className="kycInfoIconImg"
                      />
                    </div>
                    <div className="kycInfoContent">
                      <div className="kycInfoViewLabel">Nationality</div>
                      {isEntityLike ? (
                        <span className="kycInfoViewText">—</span>
                      ) : (
                        <div
                          data-tooltip-id={`sh-nat-${rowId}`}
                          data-tooltip-content={countryLabel(r?.nationality)}
                        >
                          <CountrySelect
                            placeholder=""
                            value={r?.nationality ?? ""}
                            onChange={(val) =>
                              updateRow(idx, "nationality", val || "")
                            }
                            singleValueMode="iso3"
                            className="kycCustomerDetailsHybridCountrySelect"
                            disabled={readOnly}
                            menuMinWidth={150}
                            optionFontSize={12}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  {r?.nationality && !isEntityLike ? (
                    <Tooltip
                      id={`sh-nat-${rowId}`}
                      place="bottom"
                      style={{
                        zIndex: 9999,
                        backgroundColor: "#9E9692",
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                    />
                  ) : null}
                </div>

                {/* Col 4: Domicile / Incorporation */}
                <div className="kycOwnershipCell">
                  <div className="kycInfoItem kycInfoItemAlignTop">
                    <div className="kycInfoIconLightGrey">
                      <img
                        src={iconAddress}
                        alt=""
                        className="kycInfoIconImg"
                      />
                    </div>
                    <div className="kycInfoContent">
                      <div className="kycInfoViewLabel">
                        {isEntityLike ? "Incorporation" : "Domicile"}
                      </div>
                      <div
                        data-tooltip-id={`sh-dom-${rowId}`}
                        data-tooltip-content={countryLabel(
                          displayedCountryOfResidence
                        )}
                      >
                        <CountrySelect
                          placeholder=""
                          value={displayedCountryOfResidence}
                          onChange={(val) => {
                            const nextValue = val || "";
                            if (
                              isTrust &&
                              updateTrusteeDomicileForShareholder(
                                r,
                                rowId,
                                nextValue
                              )
                            ) {
                              return;
                            }
                            updateRow(idx, "countryOfResidence", nextValue);
                          }}
                          singleValueMode="iso3"
                          className="kycCustomerDetailsHybridCountrySelect"
                          disabled={readOnly}
                          menuMinWidth={150}
                          optionFontSize={12}
                        />
                      </div>
                    </div>
                  </div>
                  {displayedCountryOfResidence ? (
                    <Tooltip
                      id={`sh-dom-${rowId}`}
                      place="bottom"
                      style={{
                        zIndex: 9999,
                        backgroundColor: "#9E9692",
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                    />
                  ) : null}
                </div>
              </div>
            );
          })}

        </div>

        {!readOnly ? (
          <div className="kycSigFooterRow" style={shareholders.length > 0 ? { marginTop: 16 } : undefined}>
            <AddShareholderButtons clientStyle />
          </div>
        ) : null}
      </>
    );
  }

  /* ---- TABLE LAYOUT (staff KYC page — unchanged) ---- */

  return (
    <>
      {entityTypeModalOpen ? (
        <EntityTypeModal
          onClose={() => setEntityTypeModalOpen(false)}
          onSelect={addEntityRow}
        />
      ) : null}
      <div className="d-flex justify-content-between align-items-end mb-2">
        <div className="OwnershipMainSectionTitle">
          Direct shareholders of {entityName}
          {titleNote ? <span className="kycOwnershipTitleNote" role="status">{titleNote}</span> : null}
        </div>

        {!readOnly ? (
          <AddShareholderButtons />
        ) : null}
      </div>

      <div
        className={
          "table-responsive kycTableWrap mb-2 " + (wrapClassName || "")
        }
      >
        <table className="table table-sm align-middle kycTbl kycTblShareholders">
          <colgroup>
            <col className="c-name" />
            <col className="c-type" />
            <col className="c-boType" />
            <col className="c-nat" />
            <col className="c-res" />
            {!hidePercentColumn ? <col className="c-pct" /> : null}
            {!readOnly ? <col className="c-actions" /> : null}
          </colgroup>

          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Shareholding capacity</th>
              <th>Nationality</th>
              <th>Residence / Incorporation</th>
              {!hidePercentColumn ? <th>%</th> : null}
              {actionTh}
            </tr>
          </thead>

          <tbody>
            {(shareholders || []).length === 0 ? (
              <tr>
                <td
                  colSpan={
                    readOnly
                      ? hidePercentColumn
                        ? 5
                        : 6
                      : hidePercentColumn
                      ? 6
                      : 7
                  }
                  className="text-muted"
                >
                  No direct shareholders.
                </td>
              </tr>
            ) : (
              (shareholders || []).map((r, idx) => {
                const rowId = ensureRowId(r, idx);

                const type = String(r?.holderType ?? "")
                  .trim()
                  .toLowerCase();
                const isEntity = type === "entity";
                const isTrust = type === "trust";
                const isFoundation = type === "foundation";
                const isTrustLike = isTrust || isFoundation;
                const isQualified = isQualifiedUboRow(r, qualifiedNodeIds);

                const iconClass =
                  type === "entity"
                    ? "bi bi-building"
                    : type === "individual"
                    ? "bi bi-person"
                    : isTrustLike
                    ? "bi bi-shield-lock"
                    : "bi bi-question-circle";

                return (
                  <tr key={rowId}>
                    <td>
                      <div className="kycQualifiedUboInputWrap">
                        <input
                          className="form-control form-control-sm"
                          value={toTitleCase(r?.name ?? "")}
                          disabled={readOnly}
                          onChange={(e) =>
                            updateRow(idx, "name", toTitleCase(e.target.value))
                          }
                        />
                      </div>
                    </td>

                    <td>
                      <div className="input-group input-group-sm kycSelectWithIcon w-100">
                        <span
                          className="input-group-text kycSelectIcon"
                          aria-hidden="true"
                        >
                          <i className={iconClass} />
                          {isQualified ? <QualifiedUboBadge /> : null}
                        </span>

                        <select
                          className="form-select form-select-sm kycTypeSelect"
                          value={r?.holderType ?? ""}
                          disabled={readOnly}
                          onChange={(e) =>
                            updateShareholderType(idx, e.target.value)
                          }
                        >
                          <option value="">Select</option>
                          <option value="Individual">Individual</option>
                          <option value="Entity">Entity</option>
                          <option value="Trust">Trust</option>
                          <option value="Foundation">Foundation</option>
                        </select>
                      </div>
                    </td>

                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={r?.beneficialOwnerType ?? "Beneficial owner"}
                        disabled={readOnly}
                        onChange={(e) =>
                          updateRow(idx, "beneficialOwnerType", e.target.value)
                        }
                      >
                        {BENEFICIAL_OWNER_TYPE_OPTIONS.map((o) => (
                          <option key={o.v} value={o.v}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td className="align-middle">
                      {isEntity || isTrustLike ? (
                        <span className="text-muted">—</span>
                      ) : (
                        <CountrySelect
                          value={r?.nationality ?? ""}
                          onChange={(val) =>
                            updateRow(idx, "nationality", val || "")
                          }
                          optionTextClassName="countryTextOptionsOwnership"
                          disabled={readOnly}
                        />
                      )}
                    </td>

                    <td className="align-middle">
                      <CountrySelect
                        value={r?.countryOfResidence ?? ""}
                        onChange={(val) =>
                          updateRow(idx, "countryOfResidence", val || "")
                        }
                        optionTextClassName="countryTextOptionsOwnership"
                        disabled={readOnly}
                      />
                    </td>

                    {!hidePercentColumn ? (
                      <td>
                        <input
                          className="form-control form-control-sm kycPctInput"
                          value={r?.ownershipPercent ?? ""}
                          disabled={readOnly}
                          {...percentInputProps}
                          onChange={(e) => updatePercent(idx, e.target.value)}
                        />
                      </td>
                    ) : null}

                    {!readOnly ? (
                      <td className="kycTblActionsTd">
                        <TrashBtn onClick={() => deleteRow(rowId)} />
                      </td>
                    ) : null}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
